import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { phpJsonString } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { fromBaseUuid } from './from-base-context';
import { decode } from './inventory-transform';

const schema = 'ft.inventory-generation.v1';
type Row = Record<string, unknown>;
const arrayLike = (value: unknown): value is Row => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? value[key] : undefined;
/** PHP isset(): present and not null. */
const set = (value: unknown, key: string): boolean => own(value, key) != null;
const trim = (value: string): string => value.replace(/^[ \t\n\r\0\x0B]+|[ \t\n\r\0\x0B]+$/g, '');
/** PHP 8 strtolower/strtoupper are ASCII-only. */
const asciiLower = (value: string): string => value.replace(/[A-Z]+/g, c => c.toLowerCase());
const scalar = (value: unknown): boolean => ['string', 'number', 'boolean'].includes(typeof value);

/** ft_dungeon_light_binding (ft3d_dungeon_lights_inc.php). */
function lightBinding(value: unknown): { id: string; part: string } | null {
    const part = own(value, 'part') ?? '';
    if (!arrayLike(value) || (part !== 'torch' && part !== 'sconce')) return null;
    const id = own(value, 'id');
    if (typeof id !== 'string' || !id.startsWith('dungeon-light:') || new TextEncoder().encode(id).length > 140) return null;
    return { id, part };
}

/** ft_normalize_inventory_generation_wall_run (inventory_generation_inc.php). A whitelist, like its twin. */
export function normalizeInventoryGenerationWallRun(value: unknown): Row | null {
    if (!arrayLike(value)) return null;
    const key = trim(phpJsonString(own(value, 'key') ?? ''));
    const storyId = trim(phpJsonString(own(value, 'storyId') ?? ''));
    const axis = asciiLower(trim(phpJsonString(own(value, 'axis') ?? '')));
    if (key === '' || (axis !== 'x' && axis !== 'y')) return null;
    const number = (raw: unknown): number | null =>
        isPhpNumeric(raw) && Number.isFinite(phpJsonFloat(raw)) ? roundPhpDecimal(phpJsonFloat(raw), 4) : null;
    const at = number(own(value, 'at')), spanMin = number(own(own(value, 'span'), 'min')), spanMax = number(own(own(value, 'span'), 'max'));
    const zMin = number(own(own(value, 'z'), 'min')), zMax = number(own(own(value, 'z'), 'max'));
    if (at === null || spanMin === null || spanMax === null || zMin === null || zMax === null) return null;
    if (!(spanMax > spanMin) || !(zMax > zMin)) return null;
    // One-release intake bridge: a feet key is valid without a retired story label; legacy keys stay verbatim.
    const parts = key.split(':');
    let floorFt = number(own(value, 'floorFt'));
    const keyFloor = parts.length === 5 ? number(parts[0]) : null;
    const feetKey = keyFloor !== null && parts[1] === axis
        && number(parts[2]) === at && number(parts[3]) === spanMin && number(parts[4]) === spanMax;
    if (floorFt === null && feetKey) floorFt = keyFloor;
    if (storyId === '' && (!feetKey || floorFt !== keyFloor)) return null;
    const result: Row = { key, axis, at, span: { min: spanMin, max: spanMax }, z: { min: zMin, max: zMax } };
    if (floorFt !== null) result.floorFt = floorFt;
    const sceneWallId = own(value, 'sceneWallId');
    if (typeof sceneWallId === 'string' && /^[A-Za-z0-9_:.-]{1,64}\n?$/.test(sceneWallId)) result.sceneWallId = sceneWallId;
    return result;
}

/** Complete ft_normalize_inventory_generation: the DAFT3D generation record on a generated row, or null (a manual row). */
export function normalizeInventoryGeneration(input: unknown): Row | null {
    if (typeof input === 'string' && trim(input) === '') return null;
    const value = typeof input === 'string' ? decode(input) : input;
    if (!arrayLike(value) || (own(value, 'schema') ?? schema) !== schema) return null;
    const roomId = trim(phpJsonString(own(value, 'roomId') ?? ''));
    const placementId = trim(phpJsonString(own(value, 'placementId') ?? ''));
    const fingerprint = asciiLower(trim(phpJsonString(own(value, 'fingerprint') ?? '')));
    if (roomId === '' || placementId === '' || !/^[0-9a-f]{64}\n?$/.test(fingerprint)) return null;
    const out: Row = { schema, roomId, placementId, fingerprint };
    if (Object.prototype.hasOwnProperty.call(value, 'pinned')) {
        if (typeof value.pinned !== 'boolean') return null;
        if (value.pinned) out.pinned = true;
    }
    for (const key of ['encounterId', 'seed', 'recipeId', 'recipeVersion', 'catalogRevision', 'requestId']) {
        if (set(value, key) && scalar(value[key]) && phpJsonString(value[key]) !== '') out[key] = phpJsonString(value[key]);
    }
    if (set(value, 'refreshNonce') && isPhpNumeric(value.refreshNonce)) out.refreshNonce = Number(phpJsonInteger(value.refreshNonce));
    if (set(value, 'lightFixture')) {
        const binding = lightBinding(value.lightFixture);
        if (!binding || placementId !== `${binding.id}:${binding.part}`) return null;
        out.lightFixture = binding;
        if (own(value, 'lightFixtureRetired') === true) out.lightFixtureRetired = true;
        if (/^[0-9a-f]{64}\n?$/.test(phpJsonString(own(value, 'initialLightFingerprint') ?? ''))) out.initialLightFingerprint = value.initialLightFingerprint;
    }
    // Birth snapshot, not a command to reapply a lock.
    if (set(value, 'initialLockStatusIds') && arrayLike(value.initialLockStatusIds)) {
        // ASCII-only like PHP strtoupper: a Unicode upper-case turns a ligature id into a valid UUID.
        const ids = Object.values(value.initialLockStatusIds).map(id => typeof id === 'string' ? id.replace(/[a-z]+/g, c => c.toUpperCase()) : '')
            .filter(id => fromBaseUuid(id));
        out.initialLockStatusIds = [...new Set(ids)].sort();
    }
    if (own(value, 'sceneryShell') === true) out.sceneryShell = true;
    const wallRun = normalizeInventoryGenerationWallRun(own(value, 'wallRun'));
    if (wallRun !== null) out.wallRun = wallRun;
    const carvedFrom = own(value, 'carvedFrom');
    if (typeof carvedFrom === 'string' && trim(carvedFrom) !== '' && new TextEncoder().encode(carvedFrom).length <= 160) out.carvedFrom = trim(carvedFrom);
    const wallBreach = normalizeInventoryGenerationWallRun(own(value, 'wallBreach'));
    if (wallBreach !== null) out.wallBreach = wallBreach;
    return out;
}
